import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';

type CardProps = {
  title?: ReactNode;
  /** Sits to the right of the title. Usually a small action. */
  action?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function Card({ title, action, footer, children, className }: CardProps) {
  return (
    /*
     * Elevation comes from `--ui-shadow-sm` alone, and it means two different
     * things by theme. In dark it is an inset hairline, because a cast shadow
     * on charcoal is nearly invisible. In light it is a two-layer cast shadow
     * and no line at all, so the paper-toned page reads as the surface behind
     * the card. Both live in the token, so this stays one class either way.
     *
     * The internal dividers below keep `border-line`: a divider is a divider in
     * both themes. It is the card's own outline that changes job.
     */
    <section className={cx('rounded-lg bg-surface shadow-sm', className)}>
      {title === undefined && action === undefined ? null : (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="text-sm font-strong tracking-caps text-muted uppercase">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-4">{children}</div>
      {footer === undefined ? null : (
        <footer className="border-t border-line px-4 py-3">{footer}</footer>
      )}
    </section>
  );
}
